import { useState } from 'react';
import 'chartjs-adapter-date-fns';
import { Box, Text, useMediaQuery } from '@chakra-ui/react';
import { Chart, LineElement, PointElement, LineController, CategoryScale, LinearScale, Title, Tooltip, Legend, TimeScale } from 'chart.js';

import LineGraph from './LineGraph';
import {
  useLineOfTotalRegisteredUsers,
  useLineOfDailyPosts,
  useLineOfTotalPlans,
  useLineOfDailyChat,
  useLineOfCreatedReferrals,
  useLineOfUsedReferrals
} from '../hooks';
import TimeRangeSelector from './TimeRangeSelector';

// Register Chart.js components
Chart.register(LineElement, PointElement, LineController, CategoryScale, LinearScale, Title, Tooltip, Legend, TimeScale);

function Dashboard() {
  const [isLargeScreen] = useMediaQuery('(min-width: 768px)');

  const [endDate, setEndDate] = useState(new Date());
  const [startDate, setStartDate] = useState(new Date(endDate.getTime() - 24 * 60 * 60 * 1000));

  const startDateString = startDate.toISOString().split('T')[0];
  const endDateString = endDate.toISOString().split('T')[0];
  const lineOfTotalRegisteredUsers = useLineOfTotalRegisteredUsers(startDateString, endDateString);
  const lineOfTotalPlans = useLineOfTotalPlans(startDateString, endDateString);
  const lineOfDailyPosts = useLineOfDailyPosts(startDateString, endDateString);
  const lineOfDailyChat = useLineOfDailyChat(startDateString, endDateString);
  const lineOfCreatedReferrals = useLineOfCreatedReferrals(startDateString, endDateString);
  const lineOfUsedReferrals = useLineOfUsedReferrals(startDateString, endDateString);

  const graphs = [
    { title: 'Total Registered Users', ...lineOfTotalRegisteredUsers },
    { title: 'Daily Posts', ...lineOfDailyPosts },
    { title: 'Total Plans', ...lineOfTotalPlans },
    { title: 'Daily Group Chat Messages', data: lineOfDailyChat.group, isLoading: lineOfDailyChat.isLoading, isError: lineOfDailyChat.isError },
    { title: 'Daily Plan Chat Messages', data: lineOfDailyChat.plan, isLoading: lineOfDailyChat.isLoading, isError: lineOfDailyChat.isError },
    { title: 'Daily Private Chat Messages', data: lineOfDailyChat.private, isLoading: lineOfDailyChat.isLoading, isError: lineOfDailyChat.isError },
    { title: 'Created Referrals', ...lineOfCreatedReferrals },
    { title: 'Used Referrals', ...lineOfUsedReferrals },
  ];

  const referralStats = [
    { label: 'Users Gained Through Referrals', value: lineOfUsedReferrals.totalUses },
    { label: 'Subscriptions Awarded For Referrals', value: lineOfUsedReferrals.totalSubscriptions }
  ];

  function onTimeRangeChange(startDate: Date, endDate: Date) {
    setStartDate(startDate);
    setEndDate(endDate);
  }

  return (
    <div style={styles.dashboard}>
      <TimeRangeSelector onTimeRangeChange={onTimeRangeChange} />

      <Box mb={4} p={4} borderRadius="md" boxShadow="sm" bg="white">
        <Text fontSize="lg" fontWeight="bold" mb={2}>Referral Statistics</Text>
        <div style={styles.statsContainer}>
          {referralStats.map((stat, index) => (
            <Box key={index} p={3} borderRadius="md" bg="gray.50">
              <Text fontSize="sm" color="gray.600">{stat.label}</Text>
              <Text fontSize="2xl" fontWeight="bold">{stat.value || 0}</Text>
            </Box>
          ))}
        </div>
      </Box>

      <div style={{ ...styles.smallGraphs, justifyContent: isLargeScreen ? 'flex-start' : 'center' }}>
        {graphs.map((graph, index) => (
          <div key={index} style={styles.graphContainer}>
            <LineGraph {...graph} />
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  statsContainer: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap"
  },
  dashboard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  smallGraphs: {
    display: "flex",
    flexWrap: "wrap",
    gap: 20,
    padding: 10,
    marginBottom: 50,
  },
  graphContainer: {
    width: 300,
    padding: 10,
    cursor: "pointer",
    boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)",
  },
};

export default Dashboard;
